import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  CompanyJobOutDTO,
  CompanyJobsFilter,
  EntityTypeEnum,
  JobCategoryEnum,
  JobStatusEnum,
  JobTypeEnum,
  LovDTO,
  Pageable,
  PermissionTypesEnum,
  ResultStatesEnum,
} from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation.tsx';
import RingilDataTable, { ColumnConfig, DateTimeFormatType, FilterConfig, FilterType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgCoCopy from '@icons/CoCopy.tsx';
import SvgDownload from '@icons/download.svg?react';
import SvgTruck from '@icons/truck.svg?react';
import SvgMail from '@icons/mail.svg?react';
import SvgCalendar from '@icons/calendar.svg?react';
import SvgMegaphone from '@icons/megaphone.svg?react';
import SvgReset from '@icons/reset-filled.svg?react';
import SvgLoader from '@app/icons/loader/Loader.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { usePushEvent } from '@hooks/usePush/usePushEvent.ts';
import { triggerHiddenIframeDownload } from '@utils/downloadViaHiddenIframe';

const TERMINAL: JobStatusEnum[] = [JobStatusEnum.DONE, JobStatusEnum.ERROR, JobStatusEnum.CANCELLED];

/** Per-category badge icon. Categories the design gives no icon to simply render the label alone. */
const CATEGORY_ICONS: Partial<Record<JobCategoryEnum, React.FC<React.SVGProps<SVGSVGElement>>>> = {
  [JobCategoryEnum.EXPEDITION]: SvgTruck,
  [JobCategoryEnum.RESERVATION]: SvgCalendar,
  [JobCategoryEnum.SUPPLIER_NOTIFICATION]: SvgMegaphone,
  [JobCategoryEnum.COMMUNICATION]: SvgMail,
};

/** Per-type badge icon. Every duplication flavour is the same act, so they share the copy icon. */
const TYPE_ICONS: Partial<Record<JobTypeEnum, React.FC<React.SVGProps<SVGSVGElement>>>> = {
  [JobTypeEnum.EXPORT]: SvgDownload,
  [JobTypeEnum.EXPEDITION_MULTI_DUPLICATION]: SvgCoCopy,
  [JobTypeEnum.MULTIDUPLICATION_V3]: SvgCoCopy,
  [JobTypeEnum.RESERVATION_MULTI_DUPLICATION]: SvgCoCopy,
  [JobTypeEnum.EXPEDITION_BULK_DISTRIBUTION]: SvgMegaphone,
};

/** The badges and their filters show the same icons, so they read as the same thing. */
const categoryIcon = (category: JobCategoryEnum, className: string) => {
  const Icon = CATEGORY_ICONS[category];
  return Icon ? <Icon className={className} /> : null;
};

const typeIcon = (jobType: JobTypeEnum, className: string) => {
  const Icon = TYPE_ICONS[jobType];
  return Icon ? <Icon className={className} /> : null;
};

/** Live counters patched from company-jobs push hints; absolute values, so applying is idempotent. */
interface JobProgressOverlay {
  status: JobStatusEnum;
  processed: number;
  total?: number;
}

/**
 * Jobs-center list ("Rozpracované úkoly"): company-wide, permission-gated. Rows come from the
 * paged REST list; progress bars update live from company-jobs channel hints, terminal/archive
 * hints trigger a debounced reload.
 */
const JobList: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { showToastMessage } = useContext(ToastContext);
  const nav = useNavigation();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { listCompanyJobs, retryJob, archiveJob, downloadExportAsyncJob, getUserJobFilters, saveUserJobFilters, deleteUserFilter, lovUsersForCompany } =
    useContext(ApiContext);

  const [companyUsers, setCompanyUsers] = useState<LovDTO[]>([]);
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: CompanyJobOutDTO[]; clear: () => void }>(null);
  const [overlay, setOverlay] = useState<Map<string, JobProgressOverlay>>(new Map());
  const reloadTimer = useRef<number | null>(null);

  const jobsRwFlag = useSecuredFlag('jobs-rw-actions', {
    kind: 'action',
    description: 'Retry / archive jobs',
    permissions: [PermissionTypesEnum.PERMISSION_JOBS_RW],
  });
  const jobsExportFlag = useSecuredFlag('jobs-export', {
    kind: 'action',
    description: 'Export jobs list (generic export API)',
    permissions: [PermissionTypesEnum.PERMISSION_JOBS_READ, PermissionTypesEnum.PERMISSION_JOBS_RW],
  });

  const reload = () => {
    setOverlay(new Map());
    tableRef.current?.reloadData();
  };

  // Row action in flight (RD-2954): the clicked button spins (RingilButton swallows clicks while
  // `loading`) and the other buttons of that row grey out, so neither a double click nor a parallel
  // action sends a second request — that used to come back from the BE as a 409. Other rows stay
  // clickable. Retry/archive stay busy until the table has refetched (see loadDataApi): on a fast
  // backend the POST can return before the second click of a double click, and until the fresh
  // rows render the old button is still there and would fire again.
  const [action, setAction] = useState<{ key: string; jobId: string } | null>(null);
  const busy = (key: string, jobId: string) => ({
    loading: action?.key === key && action.jobId === jobId,
    disabled: action != null && action.jobId === jobId && action.key !== key,
  });

  const retry = (jobId: string) => {
    setAction({ key: 'retry', jobId });
    retryJob(
      jobId,
      () => reload(),
      null,
      {
        successToast: false,
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const archive = (jobId: string) => {
    setAction({ key: 'archive', jobId });
    archiveJob(
      jobId,
      () => reload(),
      null,
      {
        successToast: false,
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const download = (jobId: string) => {
    setAction({ key: 'download', jobId });
    downloadExportAsyncJob(
      jobId,
      d => {
        setAction(null);
        if (d?.url) {
          triggerHiddenIframeDownload(d.url, () => showToastMessage(tr('JobList.downloadFailed', 'Stažení se nepodařilo'), '', 'error'));
        } else {
          showToastMessage(tr('JobList.downloadNotReady', 'Soubor ještě není připraven.'), '', 'warn');
        }
      },
      null,
      {
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const debouncedReload = () => {
    if (reloadTimer.current != null) window.clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(reload, 400);
  };

  useEffect(() => () => {
    if (reloadTimer.current != null) window.clearTimeout(reloadTimer.current);
  }, []);

  // Options for the "Vytvořil" filter — company user list is small, loaded once.
  useEffect(() => {
    if (loggedUser?.companyId == null) return;
    lovUsersForCompany('', loggedUser.companyId, data => setCompanyUsers(data ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedUser?.companyId]);

  usePushEvent(
    e => {
      if (e.type === 'job.progress') {
        const jobId = e.jobId as string;
        if (jobId == null) return;
        setOverlay(prev =>
          new Map(prev).set(jobId, {
            status: e.status as JobStatusEnum,
            processed: (e.processed as number) ?? 0,
            total: e.total as number | undefined,
          }),
        );
      } else {
        // job.finished / job.archived / job.draft.changed — row set or terminal state changed
        debouncedReload();
      }
    },
    { types: ['job.progress', 'job.finished', 'job.archived', 'job.draft.changed'], onReconnect: reload },
  );

  const loadDataApi = (filter: CompanyJobsFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<CompanyJobOutDTO>) => void) => {
    // The range calendar emits the till boundary as that day's midnight — stretch it to the end of
    // the day so the selected day itself is included.
    const effectiveFilter =
      filter?.createdAtTill != null ? { ...filter, createdAtTill: dayjs(filter.createdAtTill).endOf('day').toISOString() } : filter;
    listCompanyJobs(effectiveFilter, pageable, data => {
      // Fresh rows are in: whatever row action triggered this reload is finished (a download does
      // not reload the table, so it keeps its own spinner until the file lands).
      setAction(prev => (prev?.key === 'download' ? prev : null));
      // REST is the source of truth: drop overlay entries for rows the server reports terminal
      setOverlay(prev => {
        if (prev.size === 0) return prev;
        const next = new Map(prev);
        data.data.forEach(row => {
          if (TERMINAL.includes(row.jobStatus)) next.delete(row.jobId);
        });
        return next.size === prev.size ? prev : next;
      });
      onSuccess({ data: data.data, total: data.total, page: data.page, size: data.size });
    });
  };

  const typeLabel = (jobType: JobTypeEnum) => tr(`JobList.type.${jobType}`, `${jobType}`);
  const categoryLabel = (category: JobCategoryEnum) => tr(`JobList.category.${category}`, `${category}`);
  const rowTypeLabel = (row: CompanyJobOutDTO) =>
    row.jobType === JobTypeEnum.EXPORT && row.exportEntityType != null
      ? tr(`JobList.type.EXPORT.${row.exportEntityType}`, `Export ${row.exportEntityType}`)
      : typeLabel(row.jobType);

  // An application id keeps to one line. It holds a hyphen, which is a break opportunity, so its
  // min-content width would otherwise be just the digits after it — the table would then feel free
  // to squeeze the column and split the id across two lines. The fallback type label is prose and
  // may wrap.
  const renderIdColumn = (row: CompanyJobOutDTO) => (
    <Link
      to={nav.createNavigationLink(nav.urlFunctions.createJobDetail(row.jobId))}
      className={`text-content-brand${row.applicationId != null ? ' whitespace-nowrap' : ''}`}
    >
      {row.applicationId ?? rowTypeLabel(row)}
    </Link>
  );

  const renderStateColumn = (row: CompanyJobOutDTO) => {
    const live = overlay.get(row.jobId);
    const status = live?.status ?? row.jobStatus;
    const processed = live?.processed ?? row.processedCount;
    const total = live?.total ?? row.totalCount;

    if (status === JobStatusEnum.RUNNING || status === JobStatusEnum.PENDING) {
      const pct = total != null && total > 0 ? Math.round((processed / total) * 100) : 0;
      return (
        <div className={'flex flex-row flex-wrap items-center gap-3'}>
          <span className={'whitespace-nowrap text-sm text-content-secondary'}>{tr('JobList.processedPct', '{{pct}} % zpracováno', { pct })}</span>
          {/* Grows to the designed 270px when there is room and shrinks when there is not, rather
              than pinning a 270px floor that the action column then has to pay for. */}
          <div className={'h-2 min-w-[80px] max-w-[270px] flex-1 rounded bg-base-quaternary'}>
            <div className={'h-2 rounded bg-complementary-warning'} style={{ width: `${pct}%` }} />
          </div>
          {status === JobStatusEnum.RUNNING && <SvgLoader className={'animate-spin'} />}
        </div>
      );
    }
    if (status === JobStatusEnum.DONE && row.failedCount === 0) {
      return <COBadge variant={'filled'} type={'positive'}>{tr('JobList.state.DONE', 'Hotovo')}</COBadge>;
    }
    if (status === JobStatusEnum.DONE) {
      return <COBadge variant={'filled'} type={'warning'}>{tr('JobList.state.DONE_WITH_ERRORS', 'Hotovo s chybami ({{count}})', { count: row.failedCount })}</COBadge>;
    }
    if (status === JobStatusEnum.ERROR) {
      return (
        <COBadge variant={'filled'} type={'custom'} className={'bg-complementary-errorMedium px-2 py-1'}>
          {tr('JobList.state.ERROR', 'Chyba')}
        </COBadge>
      );
    }
    if (status === JobStatusEnum.CANCELLED) {
      return <COBadge variant={'filled'} type={'draft'}>{tr('JobList.state.CANCELLED', 'Zrušeno')}</COBadge>;
    }
    // DRAFT
    return <COBadge variant={'outlined'} type={'draft'}>{tr('JobList.state.DRAFT', 'Rozpracováno')}</COBadge>;
  };

  // Two badges: job type (Duplikace / Export) + the job category — the BE derives the category
  // from the exported entity, so both badges use the shared JobList.category.* labels.
  const renderCategoryColumn = (row: CompanyJobOutDTO) => {
    const isExport = row.jobType === JobTypeEnum.EXPORT;
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <COBadge variant={'outlined'} type={'neutral'}>
          <div className={'flex flex-row items-center gap-1'}>
            {typeIcon(row.jobType, 'h-3.5 w-3.5 shrink-0')}
            {tr(`JobList.typeBadge.${row.jobType}`, isExport ? 'Export' : 'Duplikace')}
          </div>
        </COBadge>
        <COBadge variant={'outlined'} type={'neutral'}>
          <div className={'flex flex-row items-center gap-1'}>
            {categoryIcon(row.jobCategory, 'h-3.5 w-3.5 shrink-0')}
            {categoryLabel(row.jobCategory)}
          </div>
        </COBadge>
      </div>
    );
  };

  // Name above date rather than one flowing line, so the column is only as wide as the longer of
  // the two and neither is ever split mid-token.
  const renderCreatedByColumn = (row: CompanyJobOutDTO) => (
    <div className={'flex flex-col items-start'}>
      {row.createdByName != null && <span className={'whitespace-nowrap'}>{row.createdByName}</span>}
      <span className={'whitespace-nowrap'}>{stringDateTimeWithLocaleFromApiStringInTz(row.createdAt, loggedUser?.defaultTimezoneId)}</span>
    </div>
  );

  const resultStateLabel = (state: ResultStatesEnum) =>
    ({
      [ResultStatesEnum.ERROR]: tr('JobList.resultOption.ERROR', 'Chyba'),
      [ResultStatesEnum.DONE_WITH_ERRORS]: tr('JobList.resultOption.DONE_WITH_ERRORS', 'Hotovo s chybou'),
      [ResultStatesEnum.RUNNING]: tr('JobList.resultOption.RUNNING', 'Zpracovává se'),
      [ResultStatesEnum.DRAFT]: tr('JobList.resultOption.DRAFT', 'Rozpracováno'),
      [ResultStatesEnum.DONE]: tr('JobList.resultOption.DONE', 'Hotovo'),
      [ResultStatesEnum.CANCELLED]: tr('JobList.resultOption.CANCELLED', 'Zrušeno'),
    })[state];

  const renderActionsColumn = (row: CompanyJobOutDTO) => {
    const live = overlay.get(row.jobId);
    const status = live?.status ?? row.jobStatus;
    const isTerminal = TERMINAL.includes(status);
    // A job that failed outright is offered a retry; one that finished with some rows failing is
    // offered a look at what failed instead — a blanket retry there would redo the rows that worked.
    const canRetry = status === JobStatusEnum.ERROR;
    const needsReview = status === JobStatusEnum.DONE && row.failedCount > 0;
    if (!jobsRwFlag.granted) return null;
    // Wrapping is the safety net, not the plan: the column is sized for the widest realistic row
    // (download + review + archive, measured across all six locales), but a longer translation
    // drops onto a second line instead of bleeding into the neighbouring column. items-center is
    // load-bearing too — the default `stretch` would pull the square icon-only archive button up to
    // the height of the taller labelled buttons next to it.
    return (
      <div className={'flex flex-row flex-wrap items-center justify-end gap-3 whitespace-nowrap'}>
        {row.jobType === JobTypeEnum.EXPORT && row.downloadReady && (
          <RingilButton
            mode={'filled-rectangle'}
            icon={<SvgDownload />}
            label={tr('JobList.download', 'Stáhnout')}
            dataTestId={`job-list-download-${row.applicationId}`}
            onClick={() => download(row.jobId)}
            {...busy('download', row.jobId)}
          />
        )}
        {canRetry && (
          <RingilButton
            mode={'filled-rectangle'}
            icon={<SvgReset />}
            label={tr('JobList.retry', 'Zkusit znovu')}
            dataTestId={`job-list-retry-${row.applicationId}`}
            onClick={() => retry(row.jobId)}
            {...busy('retry', row.jobId)}
          />
        )}
        {needsReview && (
          <RingilButton
            mode={'filled-rectangle'}
            label={tr('JobList.review', 'Zkontrolovat')}
            dataTestId={`job-list-review-${row.applicationId}`}
            onClick={() => nav.navigate(nav.urlFunctions.createJobDetail(row.jobId))}
          />
        )}
        {isTerminal && (
          <RingilButton
            mode={'table'}
            icon={<SvgDelete />}
            dataTestId={`job-list-archive-${row.applicationId}`}
            onClick={() => archive(row.jobId)}
            {...busy('archive', row.jobId)}
          />
        )}
      </div>
    );
  };

  const columnConfig: ColumnConfig<CompanyJobOutDTO>[] = [
    // The first three columns are proportioned after the Figma frame. The last two deliberately
    // declare no width: the table is `table-layout: auto`, so they size to their own content
    // instead of reserving a share of every row. A fixed share for the actions would be held even
    // on rows that only carry the archive icon, and Vytvořil would wrap next to visibly empty
    // space — while content sizing keeps the name and date on one line until the table really is
    // too narrow, and still lets the widest action row claim what it needs.
    { id: 'applicationId', header: 'JobList.columnId', headerDefault: 'ID úkolu', width: '14%', order: 1, render: renderIdColumn },
    { id: 'category', header: 'JobList.columnCategory', headerDefault: 'Kategorie', width: '16%', order: 2, render: renderCategoryColumn },
    { id: 'state', header: 'JobList.columnState', headerDefault: 'Výsledek', width: '32%', order: 3, render: renderStateColumn },
    { id: 'createdBy', header: 'JobList.columnCreatedBy', headerDefault: 'Vytvořil', order: 4, render: renderCreatedByColumn },
    { id: 'actions', header: '', headerDefault: '', order: 5, render: renderActionsColumn },
    // Sort-only entries: hidden columns are dropped from the rendered table but still feed the
    // sort chip (same trick as NotificationCenterList). "Rozpracovanost" is the default — jobs
    // needing attention (error / done-with-errors) come first, see the BE progressState weights.
    {
      id: 'progressState',
      header: 'JobList.sortProgressState',
      headerDefault: 'Rozpracovanosti',
      field: 'progressState',
      sortable: true,
      isDefaultSort: true,
      hidden: true,
      order: 6,
      render: () => null,
    },
    {
      id: 'createdAt',
      header: 'JobList.sortCreatedAt',
      headerDefault: 'Data vytvoření',
      field: 'createdAt',
      sortable: true,
      hidden: true,
      order: 7,
      render: () => null,
    },
  ];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr('JobList.filterApplicationId', 'ID úkolu'),
      label: tr('JobList.filterApplicationId', 'ID úkolu'),
      value: ['applicationId'],
      dataTestId: 'job-list-filter-applicationId',
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('JobList.filterType', 'Typ úkolu'),
      label: tr('JobList.filterType', 'Typ úkolu'),
      value: ['jobTypes'],
      options: Object.values(JobTypeEnum).map(jobType => ({
        label: typeLabel(jobType),
        value: jobType,
        active: true,
        icon: typeIcon(jobType, 'h-4 w-4 shrink-0'),
      })),
      dataTestId: 'job-list-filter-jobTypes',
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('JobList.filterCategory', 'Kategorie'),
      label: tr('JobList.filterCategory', 'Kategorie'),
      value: ['categories'],
      options: Object.values(JobCategoryEnum).map(category => ({
        label: categoryLabel(category),
        value: category,
        active: true,
        icon: categoryIcon(category, 'h-4 w-4 shrink-0'),
      })),
      dataTestId: 'job-list-filter-categories',
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('JobList.filterResult', 'Výsledek'),
      label: tr('JobList.filterResult', 'Výsledek'),
      value: ['resultStates'],
      options: Object.values(ResultStatesEnum).map(state => ({ label: resultStateLabel(state), value: state, active: true })),
      dataTestId: 'job-list-filter-resultStates',
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('JobList.filterCreatedBy', 'Vytvořil'),
      label: tr('JobList.filterCreatedBy', 'Vytvořil'),
      value: ['createdByIds'],
      options: companyUsers.map(user => ({ label: user.name, value: user.id, active: user.active })),
      dataTestId: 'job-list-filter-createdBy',
    },
    {
      filterType: FilterType.Calendar,
      placeholder: tr('JobList.filterCreatedAt', 'Datum'),
      label: tr('JobList.filterCreatedAt', 'Datum'),
      value: ['createdAtFrom', 'createdAtTill'],
      dateTimeFormat: DateTimeFormatType.ISOString,
      dataTestId: 'job-list-filter-createdAt',
    },
  ];

  /** Saved filters persist the raw CompanyJobsFilter — no UI-only fields on this list. */
  const saveFilters = saveUserJobFilters;

  return (
    <>
      <BreadCrumbComponent items={[{ label: tr('JobList.title', 'Rozpracované úkoly') }]} />
      <Heading title={tr('JobList.title', 'Rozpracované úkoly')} headingVariant={'h1'} />

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnConfig}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'job_list'}
        getSavedFilters={getUserJobFilters as never}
        saveFilters={saveFilters as never}
        deleteSavedFilter={deleteUserFilter}
        exportConfig={{ entityType: EntityTypeEnum.ASYNC_JOB, entityLabel: 'Jobs', secured: jobsExportFlag }}
        enableReload={true}
      />
    </>
  );
};

export default JobList;
